import { createContext, useCallback, useContext, useMemo } from 'react';
import { map, Observable, Subject, switchMap } from 'rxjs';
import { first } from 'rxjs/operators';
import { FCC } from '../../utils/react';
import { appState$ } from '../hooks/useAppState';
import { useCreation } from '../hooks/useCreation';
import { useRerender } from '../reactExtensions';
import { useCreateObservable } from './useCreateObservable';
import { useSubscription } from './useSubscription';

interface ScheduleRerenderContextValue {
  isShouldHangUpSignal$: Observable<boolean>;
}

const ScheduleRerenderContext = createContext<ScheduleRerenderContextValue>({
  isShouldHangUpSignal$: appState$.pipe(map(s => s === 'background')),
});

export const RxScheduleRerenderProvider: FCC<{
  isShouldHangUpSignal$: Observable<boolean>;
}> = props => {
  const value: ScheduleRerenderContextValue = useMemo(
    () => ({
      isShouldHangUpSignal$: props.isShouldHangUpSignal$,
    }),
    [props.isShouldHangUpSignal$],
  );

  return (
    <ScheduleRerenderContext.Provider value={value}>
      {props.children}
    </ScheduleRerenderContext.Provider>
  );
};

export const useIsShouldHangUpObservableRerender$ = (): Observable<boolean> => {
  return useContext(ScheduleRerenderContext).isShouldHangUpSignal$;
};

export function useRxScheduleRerender(): (options?: {
  immediately?: boolean;
}) => void {
  const [, rerender] = useRerender();

  const isShouldHangUp$ = useIsShouldHangUpObservableRerender$();

  const triggerSignal$ = useCreation(() => new Subject<void>(), []);

  const rerenderSignal$ = useCreateObservable(
    () =>
      triggerSignal$.pipe(
        switchMap(() =>
          isShouldHangUp$.pipe(first(shouldHangUp => !shouldHangUp)),
        ),
      ),
    [isShouldHangUp$, triggerSignal$],
    [],
  );

  useSubscription(rerenderSignal$, rerender);

  return useCallback(
    options => {
      if (options?.immediately) {
        rerender();
      } else {
        triggerSignal$.next();
      }
    },
    [triggerSignal$, rerender],
  );
}
